import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useModal } from "@/features/modals/lib";
import { useGetPersonByIdQuery, useUpdatePersonMutation } from "@/services/api";
import type { MaybeType } from "@/shared/types";
import type { AddTagModalFormValues } from "@/features/modals/ModalAddTag/schema";
import type { AddTagSchema } from "../types";
import { addTagSchema } from "../schema";

export const useTagsField = (selectedPersonId: MaybeType<string>) => {
  const { data } = useGetPersonByIdQuery(
    { id: selectedPersonId ?? "" },
    { skip: !selectedPersonId || selectedPersonId === "TEMP_ID" },
  );

  const [updatePerson, { isLoading }] = useUpdatePersonMutation();

  const { showModal, setIsModalOpen, hideModal, isModalOpen } = useModal();

  const { control, reset, getValues } = useForm<AddTagSchema>({
    defaultValues: { tags: data?.tags },
    resolver: yupResolver(addTagSchema),
  });

  const {
    append: rawAppend,
    fields,
    remove: rawRemove,
  } = useFieldArray({
    control,
    name: "tags",
  });

  useEffect(() => {
    if (data?.tags) {
      reset({ tags: data.tags });
    }
    if (selectedPersonId === "TEMP_ID") {
      reset({ tags: [] });
    }
  }, [data?.tags, reset, selectedPersonId]);

  const updatePersonTags = () => {
    if (!data) return;

    const currentTags = getValues("tags");

    updatePerson({
      ...data,
      id: data.id,
      tags: currentTags.map((tag) => ({ name: tag.name ?? "" })),
    });
  };

  const append = (value: { name: string }) => {
    rawAppend(value);
    updatePersonTags();
  };

  const remove = (index: number) => {
    rawRemove(index);
    updatePersonTags();
  };

  const onSubmit = (value: AddTagModalFormValues) => {
    append({ name: value.tag ?? "" });
    setIsModalOpen(false);
  };

  return {
    state: {
      isModalOpen,
      fields,
      isLoading: isLoading || selectedPersonId === "TEMP_ID",
    },
    methods: {
      showModal,
      setIsModalOpen,
      hideModal,
      onSubmit,
      remove,
    },
  };
};
